import { LitElement, html, css, nothing, PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { EtaChangeReviewWrapperStyles } from './eta-change-review-styles'
import { EtaComparison, formatLocalDateTimeString } from './helper'
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons'
import { VoyagePlan } from '../../../src/models/vessel-planning-model'
import { areArrivalEtasValid, ARRIVAL_ETA_VALIDATION_ERROR } from '../../../src/utils/route-utils'

@customElement('eta-comparison-table')
export class EtaComparisonTable extends LitElement {

    static styles = [EtaChangeReviewWrapperStyles]

    @property({ type: Array }) etaData: EtaComparison[] = []
    @property ({ type: Boolean }) etaIsRequested = false;
    @property({ type: Number }) planEtd?: number;
    @property({ type: Object }) plan: VoyagePlan | null = null;
    @state() private _editingIndex: number = -1
    @state() private _etaDataCache: EtaComparison[] = []
    @state() private _hasUserEdited = false;

  updated(changedProps: PropertyValues) {
    if (changedProps.has("etaData") && !this._hasUserEdited) {
      this._etaDataCache = [...this.etaData];
    }
  }

  private _onNewEtaInput(e: Event, index: number) {
    const input = e.target as HTMLInputElement;
    const inputDate = formatLocalDateTimeString(input.value);
    const updatedEtaData = [...this._etaDataCache]; // Clone array
    updatedEtaData[index] = {
      ...updatedEtaData[index],
      new: inputDate,
    };
    this._etaDataCache = updatedEtaData; // Triggers re-render
    this._hasUserEdited = true;
  }

  private _canCalculate(): boolean {
    return (
        Array.isArray(this._etaDataCache) &&
        this._etaDataCache.length >= 2 && this._hasUserEdited && this._arrivalEtasValid()
    );
  }

  private _arrivalEtasValid(): boolean {
    if (!this._arrivalEtasHaveValues()) return false;
    const pilotEta = new Date(this._etaDataCache[0].new).getTime();
    const berthEta = new Date(this._etaDataCache[1].new).getTime();
    return areArrivalEtasValid(this.plan, pilotEta, berthEta);
  }

  private _arrivalEtasHaveValues(): boolean {
    if (this._etaDataCache.length < 2) return false;
    const pilotEta = new Date(this._etaDataCache[0].new).getTime();
    const berthEta = new Date(this._etaDataCache[1].new).getTime();
    return Number.isFinite(pilotEta) && Number.isFinite(berthEta);
  }

  private _toInputValue(dateStr: string): string {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";

    const pad = (n: number) => String(n).padStart(2, "0");

    const year = d.getFullYear();
    const month = pad(d.getMonth() + 1); 
    const day = pad(d.getDate());
    const hours = pad(d.getHours());     // Local hours
    const minutes = pad(d.getMinutes()); // Local minutes

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  render() {
    return html`
      <div class="eta-comparison">
        <table>
          <thead>
            <tr>
              <th></th>
              <th>Current ETA <span>(in LT)</span></th>
              <th>${this.etaIsRequested ? html`Earliest ETA` : html`New Requested ETA`} <span>(in LT)</span></th>
            </tr>
          </thead>
          <tbody>
            ${this._etaDataCache.map(
              (item, index) => html`
                <tr>
                  <td class="column">${item.label}</td>
                  <td>${item.current}</td>
                  <td class="highlight">
                    ${this.etaIsRequested
                      ? html`
                          ${!item.new || this._editingIndex === index
                            ? html`
                                <input
                                  type="datetime-local"
                                  min=${this.planEtd
                                    ? this._toInputValue(new Date(this.planEtd).toISOString())
                                    : ""}
                                  .value=${item.new ? this._toInputValue(item.new) : ""}
                                  @blur=${(e: Event) => {
                                    this._onNewEtaInput(e, index)
                                    this._editingIndex = -1
                                  }}
                                  @keydown=${(e: KeyboardEvent) => {
                                    if (e.key === "Enter") {
                                      this._onNewEtaInput(e, index)
                                      this._editingIndex = -1
                                    }
                                  }}
                                />
                              `
                            : html`
                                <span
                                  class="eta-value"
                                  tabindex="0"
                                  @click=${() => (this._editingIndex = index)}
                                  @keydown=${(e: KeyboardEvent) => {
                                    if (e.key === "Enter") {
                                      this._editingIndex = index;
                                    }
                                  }}
                                >
                                  ${item.new}
                                </span>
                                <button
                                  class="edit-btn"
                                  @click=${() => (this._editingIndex = index)}
                                  title="Edit"
                                >
                                  ${SvgIcons.pencil}
                                </button>
                              `}
                        `
                      : html`${item.new}`}
                  </td>
                </tr>
              `
            )}
          </tbody>
        </table>
      </div>
      <div>
        ${this.etaIsRequested && this._arrivalEtasHaveValues() && !this._arrivalEtasValid()
          ? html`<span class="validation-text">${ARRIVAL_ETA_VALIDATION_ERROR}</span>`
          : nothing}
        ${this.etaIsRequested ? 
          html`
            <mc-button 
              appearance="secondary"
              variant="filled"
              ?disabled=${!this._canCalculate()}
              @click=${() => {
                if (!this._canCalculate()) return;
                this.dispatchEvent(new CustomEvent("eta-requested", {
                detail:{
                  pilotEta: this._toInputValue(this._etaDataCache[0].new),
                  berthEta: this._toInputValue(this._etaDataCache[1].new)
                },
                bubbles: true,
                composed: true
                }))
              }}
            >
                Calculate
            </mc-button>
          `
          : nothing
        }
      </div>
    `
  }
}
